import { queryOptions } from '@tanstack/react-query';
import { Transfer } from '@orbis-systems/orbis-api-middleware';
import type { NewTransferFormValues } from '@/features/money-transfers/schemas/new-transfer-schema';
import { orbis, orbisMiddleware, queryClient } from '@/queries/api';

export type MoneyTransfersQueryParams = {
    accountNumber?: string;
    start?: number;
    perPage?: number;
};

export type CancelTransferParams = {
    accountNumber: string;
    transferId: string;
};

export type CancelIfscBankLinkParams = {
    id: number;
    account: string;
    reason?: string;
};

export const moneyTransfersQueries = {
    all: () => ['money-transfers'] as const,
    list: (params: MoneyTransfersQueryParams = {}) =>
        queryOptions<Transfer[]>({
            queryKey: [...moneyTransfersQueries.all(), 'list', params],
            queryFn: () =>
                params.accountNumber
                    ? orbisMiddleware.account.getAccountTransfers(params.accountNumber)
                    : orbisMiddleware.branch.getBranchMoneyTransfers(params.start, params.perPage),
        }),
    availableCashForWithdrawal: (accountNumber: string) =>
        queryOptions<number>({
            queryKey: [...moneyTransfersQueries.all(), 'available-cash', accountNumber],
            queryFn: () => orbisMiddleware.account.getAccountWithdrawableCash(accountNumber),
        }),
};

export const moneyTransfersMutations = {
    cancel: ({ accountNumber, transferId }: CancelTransferParams) =>
        orbisMiddleware.account.cancelTransaction(accountNumber, transferId),
    cancelIfscBankLink: ({ id, account, reason }: CancelIfscBankLinkParams) =>
        orbisMiddleware.account.cancelIfscBankLink(id, account, reason),
    deposit: (values: NewTransferFormValues) =>
        orbisMiddleware.account.depositCash({
            ...values,
            contribution: {
                type: values.contributionType,
                priorYear: values.contributionPriorYear,
            },
        }),
    withdraw: (values: NewTransferFormValues) =>
        orbisMiddleware.account.withdrawCash({
            ...values,
            distribution: {
                institution: values.institution,
                reason: values.reason,
                federalTax: {
                    type: values.federalTaxType,
                    amount: values.federalTaxAmount,
                },
                stateTax: {
                    type: values.stateTaxType,
                    amount: values.stateTaxAmount,
                },
            },
        }),
    wireWithdraw: async (values: NewTransferFormValues) => {
        const {
            accountNumber,
            amount,
            recipientBankId,
            recipientBankType,
            beneficiaryAccount,
            beneficiaryAccountType,
        } = values;

        const response = await orbis.getApiClient().post('transfer/wire/outgoing', {
            account: accountNumber,
            amount,
            international: false,
            disbursementType: 'Partial',
            recipientBank: {
                identifier: { type: recipientBankType, id: recipientBankId },
            },
            beneficiary: {
                account: beneficiaryAccount,
                accountType: beneficiaryAccountType,
                thirdParty: false,
            },
        });

        return response.data;
    },
};

export function invalidateMoneyTransfersQueries() {
    return queryClient.invalidateQueries({ queryKey: moneyTransfersQueries.all() });
}
